/**
 * ElasticSearch Service
 *
 * Main service that provides search functionality using ElasticSearch.
 * Mirrors the interface of Algolia.js for easy switching.
 *
 * This module provides:
 * - Ads search with filters, sorting, and pagination
 * - Location autocomplete search
 * - Similar ads search
 */

import { createElasticClient } from "./elasticClient"
import { ES_CONFIG, getAdsIndexName, getLocationsIndexName } from "./config"
import { transpileSearchResponse, transpileLocationSearchResponse } from "./transpileResponse"
import { getSortForKey, SORT_OPTIONS, SortKeys } from "./sortValues"
import { buildLocationSearchQuery, getSubLocationsFilter } from "./locationSearchQuery"

/**
 * Creates a searcher object that mimics Algolia's index.search() interface
 * This allows drop-in replacement of Algolia search calls
 */
class ElasticSearcher {
  constructor(indexName, options = {}) {
    this.indexName = indexName
    this.client = createElasticClient()
    this.defaultSort = options.sort || SortKeys.POPULAR
  }

  /**
   * Search method that mimics Algolia's search interface
   *
   * @param {string} query - Search query string
   * @param {Object} options - Search options
   * @param {number} [options.page=0] - Page number (0-indexed like Algolia)
   * @param {number} [options.hitsPerPage=25] - Results per page
   * @param {string} [options.filters] - Filter string (Algolia format)
   * @param {string} [options.numericFilters] - Numeric filters (bedroom filters)
   * @param {Array} [options.facets] - Facet attributes
   * @param {string} [options.sortKey] - Sort key
   * @returns {Promise<Object>} Search results in normalized format
   */
  async search(query = "", options = {}) {
    const { page = 0, hitsPerPage = 25, filters, numericFilters, facets, sortKey } = options

    // Build ES bool query directly from Algolia-style filters
    const esFilters = this.parseAlgoliaFiltersToES(filters, numericFilters)

    // ES hard limit: from + size must be <= 10000. Clamp page so we never exceed it.
    const ES_MAX_RESULT_WINDOW = 10000
    const safePage = Math.min(page, Math.floor((ES_MAX_RESULT_WINDOW - hitsPerPage) / hitsPerPage))

    // Build ES request with parsed filters
    const esRequest = {
      from: safePage * hitsPerPage,
      size: hitsPerPage,
      track_total_hits: true,
    }

    // Add bool query if we have filters
    if (esFilters && Object.keys(esFilters).length > 0) {
      esRequest.query = { bool: esFilters }
    }

    // Add query clause if text search
    // Uses multiple match strategies to replicate Algolia's search behavior:
    // - Single word: lenient matching (prefix, fuzzy) for partial matches
    // - Multi-word: strict matching (all words must match) like Algolia
    if (query && query.trim()) {
      const trimmedQuery = query.trim()
      const words = trimmedQuery.split(/\s+/)
      const isSingleWord = words.length === 1

      // Build multiple should clauses to match Algolia's behavior
      const shouldClauses = []

      // 1. Prefix matching using bool_prefix
      // For single word: allows "citi" to match "city", "citizen", etc.
      // For multi-word: requires all word prefixes to match
      shouldClauses.push({
        multi_match: {
          query: trimmedQuery,
          type: "bool_prefix",
          fields: [
            "query^4",           // Main searchable text field (highest priority)
            "query._2gram",      // N-gram for partial matching
            "query._3gram",
            "title^3",           // Property title
            "location.name^2",   // Location name
            "externalID",        // Property ID
          ],
          operator: "and",       // Require all terms for multi-word queries
        },
      })

      // 2. Phrase prefix for exact phrase matching
      shouldClauses.push({
        match_phrase_prefix: {
          query: {
            query: trimmedQuery,
            boost: 5,
          },
        },
      })

      // 3. Fuzzy matching for typo tolerance
      // operator: "and" ensures all words must match (like Algolia)
      shouldClauses.push({
        multi_match: {
          query: trimmedQuery,
          type: "most_fields",
          fields: ["query", "title", "location.name"],
          fuzziness: "AUTO",
          operator: "and",
          boost: 2,
        },
      })

      // 4. Cross-fields match for words split across fields
      // operator: "and" ensures all terms must be found
      shouldClauses.push({
        multi_match: {
          query: trimmedQuery,
          type: "cross_fields",
          fields: ["query", "title", "location.name"],
          operator: "and",
          boost: 1.5,
        },
      })

      // 5. Single-word only: wildcard for "contains" matching
      // This helps match partial words like "citi" -> "city"
      // Only for single words to avoid explosion with multi-word queries
      if (isSingleWord && trimmedQuery.length >= 3 && trimmedQuery.length <= 12) {
        shouldClauses.push({
          wildcard: {
            query: {
              value: `*${trimmedQuery.toLowerCase()}*`,
              boost: 0.5,
              case_insensitive: true,
            },
          },
        })
      }

      // 6. Exact term match for externalID (property ID search)
      shouldClauses.push({
        term: {
          externalID: {
            value: trimmedQuery,
            boost: 10,
          },
        },
      })

      // Wrap in bool with should + minimum_should_match
      const textQuery = {
        bool: {
          should: shouldClauses,
          minimum_should_match: 1,
        },
      }

      if (!esRequest.query) {
        esRequest.query = { bool: { must: [textQuery] } }
      } else if (esRequest.query.bool.must) {
        esRequest.query.bool.must.push(textQuery)
      } else {
        esRequest.query.bool.must = [textQuery]
      }
    }

    // Add sort - always specify to match Algolia's customRanking behavior
    // Algolia customRanking: [productScore ASC, score DESC, createdAt DESC, externalID ASC]
    const sortOption = getSortForKey(sortKey || this.defaultSort)
    if (sortOption && sortOption.attributes) {
      esRequest.sort = sortOption.attributes.map((attr) => {
        // Handle script-based sorting for externalID (numeric conversion)
        if (attr.name === "_script" && attr.script) {
          return {
            _script: {
              type: attr.script.type,
              script: attr.script.script,
              order: attr.order,
            },
          }
        }
        // Standard field sorting
        return { [attr.name]: { order: attr.order } }
      })
    }

    // Use multiSearch to match Mustang's _msearch endpoint pattern
    const responses = await this.client.multiSearch([
      { indexName: this.indexName, request: esRequest }
    ])

    // multiSearch returns an array, get the first response
    const response = responses[0]

    // Transpile response to Algolia-like format
    return transpileSearchResponse(response, {
      page: page + 1,
      hitsPerPage,
      indexName: this.indexName,
    })
  }

  /**
   * Parse Algolia filter string and numericFilters to ES bool query
   * Handles Lamudi's actual filter format:
   * - filters: "purpose:for-sale AND category.slug:houses AND (location.externalID:1 OR location.externalID:2)"
   * - filters: "externalID:1 OR externalID:2" (OR without parentheses)
   * - filters: "NOT objectID:123 AND NOT objectID:456" (exclusions from discoverMore)
   * - numericFilters: "(rooms=1,rooms=2,rooms>=6)"
   *
   * @param {string} filterString - Algolia filter string
   * @param {string} numericFilters - Numeric filters (bedroom filters)
   * @returns {Object} ES bool query clauses { must: [], should: [], must_not: [] }
   */
  parseAlgoliaFiltersToES(filterString, numericFilters) {
    const must = []
    const should = []
    const mustNot = []

    if (!filterString && !numericFilters) {
      return {}
    }

    if (filterString) {
      // Check if entire filter is just OR conditions (no AND)
      // e.g., "externalID:1 OR externalID:2"
      if (!filterString.includes(" AND ") && filterString.includes(" OR ")) {
        const orParts = filterString.split(" OR ")
        const orClauses = []

        orParts.forEach((orPart) => {
          const clause = this.parseSimpleCondition(orPart.trim())
          if (clause) {
            orClauses.push(clause)
          }
        })

        if (orClauses.length > 0) {
          must.push({
            bool: {
              should: orClauses,
              minimum_should_match: 1,
            },
          })
        }
      } else {
        // Split by AND, but preserve content inside parentheses
        const parts = this.splitByAND(filterString)

        parts.forEach((part) => {
          const trimmed = part.trim()

          // Handle NOT conditions: NOT objectID:123
          if (trimmed.startsWith("NOT ")) {
            const condition = trimmed.slice(4).trim() // Remove "NOT "
            const clause = this.parseSimpleCondition(condition)
            if (clause) {
              mustNot.push(clause)
            }
            return
          }

          // Handle OR conditions in parentheses: (location.externalID:1 OR location.externalID:2)
          if (trimmed.startsWith("(") && trimmed.endsWith(")") && trimmed.includes(" OR ")) {
            const inner = trimmed.slice(1, -1) // Remove parentheses
            const orParts = inner.split(" OR ")
            const orClauses = []

            orParts.forEach((orPart) => {
              const clause = this.parseSimpleCondition(orPart.trim())
              if (clause) {
                orClauses.push(clause)
              }
            })

            if (orClauses.length > 0) {
              must.push({
                bool: {
                  should: orClauses,
                  minimum_should_match: 1,
                },
              })
            }
            return
          }

          // Handle simple conditions
          const clause = this.parseSimpleCondition(trimmed)
          if (clause) {
            must.push(clause)
          }
        })
      }
    }

    // Handle numericFilters for bedrooms: (rooms=1,rooms=2,rooms>=6)
    if (numericFilters) {
      const bedroomClauses = this.parseNumericFilters(numericFilters)
      if (bedroomClauses.length > 0) {
        must.push({
          bool: {
            should: bedroomClauses,
            minimum_should_match: 1,
          },
        })
      }
    }

    const result = {}
    if (must.length > 0) {
      result.must = must
    }
    if (should.length > 0) {
      result.should = should
      result.minimum_should_match = 1
    }
    if (mustNot.length > 0) {
      result.must_not = mustNot
    }

    return result
  }

  /**
   * Split filter string by AND while preserving parentheses content
   */
  splitByAND(filterString) {
    const parts = []
    let current = ""
    let depth = 0

    for (let i = 0; i < filterString.length; i++) {
      const char = filterString[i]

      if (char === "(") {
        depth++
        current += char
      } else if (char === ")") {
        depth--
        current += char
      } else if (depth === 0 && filterString.slice(i, i + 5) === " AND ") {
        parts.push(current.trim())
        current = ""
        i += 4 // Skip " AND "
      } else {
        current += char
      }
    }

    if (current.trim()) {
      parts.push(current.trim())
    }

    return parts
  }

  /**
   * Parse a simple condition like "attribute:value" or "price > 1000"
   */
  parseSimpleCondition(condition) {
    // Algolia to ES field name mapping
    const fieldMapping = {
      objectID: "id", // Algolia uses objectID, ES uses id
    }

    // Handle range: price > 1000 or price < 5000 or area >= 100
    const rangeMatch = condition.match(/^(\w+(?:\.\w+)*)\s*(>=|<=|>|<)\s*(\d+(?:\.\d+)?)$/)
    if (rangeMatch) {
      let [, attr, op, val] = rangeMatch
      // Apply field mapping
      attr = fieldMapping[attr] || attr
      const rangeClause = {}

      if (op === ">") {
        rangeClause.gt = Number(val)
      } else if (op === ">=") {
        rangeClause.gte = Number(val)
      } else if (op === "<") {
        rangeClause.lt = Number(val)
      } else if (op === "<=") {
        rangeClause.lte = Number(val)
      }

      return { range: { [attr]: rangeClause } }
    }

    // Handle exact match: attribute:value
    if (condition.includes(":") && !condition.includes(">") && !condition.includes("<")) {
      const colonIndex = condition.indexOf(":")
      let attr = condition.slice(0, colonIndex).trim()
      let value = condition.slice(colonIndex + 1).trim().replace(/"/g, "")

      // Apply field mapping (objectID -> id)
      attr = fieldMapping[attr] || attr

      // For externalID fields, keep as string since ES indexes them as strings
      // For other fields, try to convert to number if applicable
      const isExternalIdField = attr.toLowerCase().includes('externalid')
      if (!isExternalIdField && !isNaN(value) && value !== "") {
        value = Number(value)
      }

      return { term: { [attr]: value } }
    }

    return null
  }

  /**
   * Parse numericFilters for bedrooms: (rooms=1,rooms=2,rooms>=6)
   */
  parseNumericFilters(numericFilters) {
    const clauses = []

    // Remove outer parentheses
    let inner = numericFilters.trim()
    if (inner.startsWith("(") && inner.endsWith(")")) {
      inner = inner.slice(1, -1)
    }

    // Split by comma
    const parts = inner.split(",")

    parts.forEach((part) => {
      const trimmed = part.trim()

      // Handle rooms>=6
      const gteMatch = trimmed.match(/^(\w+)>=(\d+)$/)
      if (gteMatch) {
        const [, attr, val] = gteMatch
        clauses.push({ range: { [attr]: { gte: Number(val) } } })
        return
      }

      // Handle rooms<=6
      const lteMatch = trimmed.match(/^(\w+)<=(\d+)$/)
      if (lteMatch) {
        const [, attr, val] = lteMatch
        clauses.push({ range: { [attr]: { lte: Number(val) } } })
        return
      }

      // Handle rooms=1
      const eqMatch = trimmed.match(/^(\w+)=(\d+)$/)
      if (eqMatch) {
        const [, attr, val] = eqMatch
        clauses.push({ term: { [attr]: Number(val) } })
        return
      }
    })

    return clauses
  }
}

/**
 * Location searcher for autocomplete
 * Implements the same query structure as Mustang for location search
 * Uses _msearch endpoint to match Mustang's API pattern
 */
class LocationSearcher {
  constructor(indexName) {
    this.indexName = indexName
    this.client = createElasticClient()
  }

  /**
   * Search locations using Mustang's query structure
   * Uses multiSearch (_msearch) to match Mustang's API pattern
   *
   * @param {string} query - Search query
   * @param {Object} options - Search options (Algolia-style)
   * @param {string} [options.filters] - Algolia-style filter string (e.g. "hierarchy.externalID:123 AND level>2")
   * @param {number} [options.hitsPerPage=20] - Number of results
   * @returns {Promise<Object>} Location search results in Algolia-like format
   */
  async search(query = "", options = {}) {
    const { filters, hitsPerPage = 20 } = options

    // Build the ES request using Mustang's exact query structure
    const esRequest = buildLocationSearchQuery(query, {
      filters,
      hitsPerPage,
    })

    try {
      // Use multiSearch to match Mustang's _msearch endpoint pattern
      const responses = await this.client.multiSearch([
        { indexName: this.indexName, request: esRequest }
      ])
      
      // multiSearch returns an array, get the first response
      const response = responses[0]
      return transpileLocationSearchResponse(response)
    } catch (error) {
      throw error
    }
  }
}

/**
 * ElasticSearch service object
 * Provides static methods similar to Algolia service
 */
export const ElasticSearch = {
  /**
   * Create an ads searcher instance
   * @param {string} [sort='popular'] - Default sort option
   * @returns {ElasticSearcher} Searcher instance
   */
  createAdsSearcher(sort = SortKeys.POPULAR) {
    return new ElasticSearcher(getAdsIndexName(), { sort })
  },

  /**
   * Create a locations searcher instance
   * @returns {LocationSearcher} Location searcher instance
   */
  createLocationsSearcher() {
    return new LocationSearcher(getLocationsIndexName())
  },

  /**
   * Get ads index (alias for createAdsSearcher for Algolia compatibility)
   * @param {string} [sort='popular'] - Sort option
   * @returns {ElasticSearcher} Searcher instance
   */
  getAdsIndex(sort = SortKeys.POPULAR) {
    return this.createAdsSearcher(sort)
  },

  /**
   * Get locations index (alias for createLocationsSearcher)
   * @returns {LocationSearcher} Location searcher instance
   */
  getLocationsIndex() {
    return this.createLocationsSearcher()
  },

  /**
   * Direct search function for ads
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchAds(options = {}) {
    const searcher = this.createAdsSearcher(options.sortKey)
    return searcher.search(options.query || "", options)
  },

  /**
   * Direct search function for locations
   * @param {string} query - Search query
   * @param {Object} options - Search options
   * @returns {Promise<Object>} Search results
   */
  async searchLocations(query, options = {}) {
    const searcher = this.createLocationsSearcher()
    return searcher.search(query, options)
  },
}

// Export utilities
export { SORT_OPTIONS, SortKeys, getSortForKey }
export { ES_CONFIG }
export { getSubLocationsFilter }

export default ElasticSearch
